import { createContext, useContext, useState } from "react";
import { Import, Menu, Settings, User, Users } from "lucide-react";
import { CanAccess, useTranslate, useUserMenu } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";
import { RefreshButton } from "@/components/admin/refresh-button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { ImportPage } from "../misc/ImportPage";
import { useIsClient } from "../misc/useRole";

const Header = () => {
  const { darkModeLogo, lightModeLogo, title } = useConfigurationContext();
  const location = useLocation();
  const isClient = useIsClient();

  let currentPath: string | boolean = "/";
  if (matchPath("/", location.pathname)) {
    currentPath = "/";
  } else if (matchPath("/contacts/*", location.pathname)) {
    currentPath = "/contacts";
  } else if (matchPath("/companies/*", location.pathname)) {
    currentPath = "/companies";
  } else if (matchPath("/deals/*", location.pathname)) {
    currentPath = "/deals";
  } else if (matchPath("/clients/*", location.pathname)) {
    currentPath = "/clients";
  } else if (matchPath("/calendar", location.pathname)) {
    currentPath = "/calendar";
  } else if (matchPath("/proposals/*", location.pathname)) {
    currentPath = "/proposals";
  } else if (matchPath("/reports", location.pathname)) {
    currentPath = "/reports";
  } else if (matchPath("/brand", location.pathname)) {
    currentPath = "/brand";
  } else {
    currentPath = false;
  }

  return (
    <>
      <nav className="grow">
        <header className="border-b border-border bg-background">
          <div className="px-4">
            <div className="flex justify-between items-center flex-1">
              <Link
                to="/"
                className="flex items-center gap-2 text-secondary-foreground no-underline"
              >
                <img
                  className="[.light_&]:hidden h-7"
                  src={darkModeLogo}
                  alt={title}
                />
                <img
                  className="[.dark_&]:hidden h-7"
                  src={lightModeLogo}
                  alt={title}
                />
              </Link>
              <nav className="hidden lg:flex">
                <NavItems currentPath={currentPath} isClient={isClient} />
              </nav>
              <div className="flex items-center">
                <MobileMenu currentPath={currentPath} isClient={isClient} />
                <ThemeModeToggle />
                <RefreshButton />
                <UserMenu>
                  <CanAccess resource="clients" action="list">
                    <ProfileMenu />
                  </CanAccess>
                  <CanAccess resource="sales" action="list">
                    <UsersMenu />
                  </CanAccess>
                  <CanAccess resource="configuration" action="edit">
                    <SettingsMenu />
                  </CanAccess>
                  <CanAccess resource="contacts" action="list">
                    <ImportFromJsonMenuItem />
                  </CanAccess>
                </UserMenu>
              </div>
            </div>
          </div>
        </header>
      </nav>
    </>
  );
};

type NavProps = { currentPath: string | boolean; isClient: boolean };

// Same entries for the desktop bar and the mobile menu; CanAccess filters by role.
const NavItems = ({ currentPath, isClient }: NavProps) => {
  const translate = useTranslate();
  return (
    <>
      {isClient ? (
        <>
          <NavigationTab
            label={translate("crm.portal.menu")}
            to="/"
            isActive={currentPath === "/"}
          />
          <NavigationTab
            label={translate("resources.client_reports.menu")}
            to="/reports"
            isActive={currentPath === "/reports"}
          />
          <NavigationTab
            label={translate("resources.brand_assets.menu")}
            to="/brand"
            isActive={currentPath === "/brand"}
          />
        </>
      ) : null}
      <CanAccess resource="clients" action="list">
        <NavigationTab
          label={translate("ra.page.dashboard")}
          to="/"
          isActive={currentPath === "/"}
        />
        <NavigationTab
          label={translate("resources.content_pieces.menu")}
          to="/calendar"
          isActive={currentPath === "/calendar"}
        />
        <NavigationTab
          label={translate("resources.clients.name", {
            smart_count: 2,
          })}
          to="/clients"
          isActive={currentPath === "/clients"}
        />
      </CanAccess>
      <CanAccess resource="contacts" action="list">
        <NavigationTab
          label={translate("resources.contacts.name", {
            smart_count: 2,
          })}
          to="/contacts"
          isActive={currentPath === "/contacts"}
        />
      </CanAccess>
      <CanAccess resource="companies" action="list">
        <NavigationTab
          label={translate("resources.companies.name", {
            smart_count: 2,
          })}
          to="/companies"
          isActive={currentPath === "/companies"}
        />
      </CanAccess>
      <CanAccess resource="deals" action="list">
        <NavigationTab
          label={translate("resources.deals.name", {
            smart_count: 2,
          })}
          to="/deals"
          isActive={currentPath === "/deals"}
        />
      </CanAccess>
      <CanAccess resource="proposals" action="list">
        <NavigationTab
          label={translate("resources.proposals.menu")}
          to="/proposals"
          isActive={currentPath === "/proposals"}
        />
      </CanAccess>
    </>
  );
};

// Below the lg breakpoint the tabs move into a side menu.
const MobileMenu = (props: NavProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const translate = useTranslate();
  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label={translate("ra.action.open_menu")}
          className="mr-1 grid size-9 place-items-center rounded-full text-foreground hover:bg-accent lg:hidden"
        >
          <Menu className="size-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="w-72 bg-background p-0">
        <SheetTitle className="sr-only">
          {translate("crm.navigation.label")}
        </SheetTitle>
        <NavVariantContext.Provider
          value={{ variant: "menu", onNavigate: () => setIsOpen(false) }}
        >
          <nav className="flex flex-col gap-1 p-4 pt-14">
            <NavItems {...props} />
          </nav>
        </NavVariantContext.Provider>
      </SheetContent>
    </Sheet>
  );
};

const NavVariantContext = createContext<{
  variant: "tab" | "menu";
  onNavigate?: () => void;
}>({ variant: "tab" });

const NavigationTab = ({
  label,
  to,
  isActive,
}: {
  label: string;
  to: string;
  isActive: boolean;
}) => {
  const { variant, onNavigate } = useContext(NavVariantContext);
  if (variant === "menu") {
    return (
      <Link
        to={to}
        onClick={onNavigate}
        className={`rounded-xl px-4 py-3 text-base font-medium no-underline transition-colors ${
          isActive
            ? "bg-primary text-primary-foreground"
            : "text-foreground hover:bg-accent"
        }`}
      >
        {label}
      </Link>
    );
  }
  return (
    <Link
      to={to}
      className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
        isActive
          ? "text-secondary-foreground border-secondary-foreground"
          : "text-secondary-foreground/70 border-transparent hover:text-secondary-foreground/80"
      }`}
    >
      {label}
    </Link>
  );
};

const UsersMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<UsersMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/sales" className="flex items-center gap-2">
        <Users />
        {translate("resources.sales.name", { smart_count: 2 })}
      </Link>
    </DropdownMenuItem>
  );
};

const ProfileMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ProfileMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/profile" className="flex items-center gap-2">
        <User />
        {translate("crm.profile.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const SettingsMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<SettingsMenu> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/settings" className="flex items-center gap-2">
        <Settings />
        {translate("crm.settings.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const ImportFromJsonMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ImportFromJsonMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ImportPage.path} className="flex items-center gap-2">
        <Import />
        {translate("crm.header.import_data")}
      </Link>
    </DropdownMenuItem>
  );
};

export default Header;
